import React, { useEffect } from 'react';
import { NavLink, Outlet, Navigate, Link } from 'react-router-dom';
import {
  FaHome, FaMicrochip, FaServer, FaVideo, FaBolt, FaMagic,
  FaLeaf, FaShoppingBag, FaBoxOpen, FaThLarge, FaLifeRing, FaUserCog,
} from 'react-icons/fa';
import { useAuthStore } from '../../store/authStore';

/**
 * E1 — website customer-dashboard foundation.
 *
 * A DIFFERENT UX from the mobile app / customer portal (a sidebar web
 * layout, not tabs or a card-based mobile flow), but operating against
 * the exact same canonical data via the exact same /api/mobile/*
 * endpoints — see src/services/endpoints.js. Per the explicit
 * instruction, this does not duplicate the mobile UI; it's a new,
 * website-appropriate presentation of the same underlying account.
 *
 * ARCHITECTURAL STATUS (E2.1, see E2_PHASE0_ARCHITECTURE_PLAN.md §3-§5
 * and E2.1_CUSTOMER_PORTAL_CONSOLIDATION_REPORT.md): frontend/customer/
 * is now the canonical customer portal. Every page this layout routes to
 * (Homes, Gateways, Shop, Orders, CompatibleDevices, Support/Account —
 * Devices/Cameras/Automations/Scenes/Energy/Dashboard already had a
 * superior equivalent before E2.1) has an equivalent now live in
 * frontend/customer/src/pages/. This tree is intentionally NOT deleted
 * yet (safe-removal verification is still pending — see that report's
 * §9) and still functions correctly; it is not being extended with new
 * functionality going forward. Do not add new dashboard pages here.
 */

const NAV = [
  { to: '/dashboard', end: true, icon: FaHome, label: 'Dashboard' },
  { to: '/dashboard/homes', icon: FaThLarge, label: 'My Homes' },
  { to: '/dashboard/devices', icon: FaMicrochip, label: 'My Devices' },
  { to: '/dashboard/gateways', icon: FaServer, label: 'Gateways' },
  { to: '/dashboard/cameras', icon: FaVideo, label: 'Cameras' },
  { to: '/dashboard/automations', icon: FaMagic, label: 'Automations' },
  { to: '/dashboard/scenes', icon: FaBolt, label: 'Scenes' },
  { to: '/dashboard/energy', icon: FaLeaf, label: 'Energy' },
  { to: '/dashboard/shop', icon: FaShoppingBag, label: 'Shop' },
  { to: '/dashboard/orders', icon: FaBoxOpen, label: 'Orders' },
  { to: '/dashboard/compatible-devices', icon: FaThLarge, label: 'Compatible Devices' },
  { to: '/dashboard/support', icon: FaLifeRing, label: 'Support' },
  { to: '/dashboard/account', icon: FaUserCog, label: 'Account' },
];

export default function DashboardLayout() {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center text-white/60">
        Loading your account…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white flex">
      <aside className="w-64 shrink-0 border-r border-white/10 hidden md:flex md:flex-col">
        <Link to="/" className="flex items-center gap-3 px-6 py-6">
          <img src="/logo-new.png" alt="Sheba Smart Home" className="h-9 w-auto" />
        </Link>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, end, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-[#00d4ff]/15 text-[#00d4ff]' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <Icon className="shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-white/10 text-xs text-white/40">
          Signed in as {user.email}
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-6 md:p-10">
        <Outlet />
      </main>
    </div>
  );
}
